// Detects when the server-side build hash drifts from the one baked
// into the loaded bundle. Triggers on three signals:
//
//   1. setInterval every POLL_MS — catches users who leave the tab
//      open across multiple deploys.
//   2. visibilitychange / window focus — catches the common "left for
//      lunch, came back" case so the banner appears within seconds of
//      returning to the tab.
//   3. First mount — same idea for "opened a stale tab this morning".
//
// Once a drift is detected the hook stops polling — re-checking serves
// no purpose, the banner is already up.
import { useEffect, useRef, useState } from 'react';

interface VersionResponse {
  buildId: string;
  builtAt: number;
}

interface UseVersionWatcherReturn {
  updateAvailable: boolean;
  newBuildId: string | null;
  builtAt: number | null;
  reload: () => void;
  dismiss: () => void;
}

const POLL_MS = 5 * 60 * 1000;
const DISMISS_KEY = 'badges.update.dismissedBuildId';

export function useVersionWatcher(): UseVersionWatcherReturn {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [newBuildId, setNewBuildId] = useState<string | null>(null);
  const [builtAt, setBuiltAt] = useState<number | null>(null);
  // Ref so the polling callback doesn't fight a stale closure on the
  // state setter when it fires after a re-render.
  const stoppedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      if (stoppedRef.current || cancelled) return;
      try {
        // cache: 'no-store' belt + ?t= suspenders so neither the
        // browser nor any CDN intermediary serves us a stale copy.
        const res = await fetch(`/version.json?t=${Date.now()}`, {
          cache: 'no-store',
          credentials: 'omit',
        });
        if (!res.ok) return;
        const data = (await res.json()) as VersionResponse;
        if (!data?.buildId) return;
        if (data.buildId === __BUILD_ID__) return;
        // A user can dismiss a specific buildId for the session; a
        // newer build than the dismissed one re-triggers the banner.
        const dismissed = sessionStorage.getItem(DISMISS_KEY);
        if (dismissed === data.buildId) return;
        stoppedRef.current = true;
        setNewBuildId(data.buildId);
        setBuiltAt(data.builtAt ?? null);
        setUpdateAvailable(true);
      } catch {
        // Network blip, offline, or version.json not deployed yet —
        // silent. We'll try again on the next tick.
      }
    }

    void check();
    const interval = window.setInterval(check, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  return {
    updateAvailable,
    newBuildId,
    builtAt,
    reload: () => window.location.reload(),
    dismiss: () => {
      if (newBuildId) sessionStorage.setItem(DISMISS_KEY, newBuildId);
      setUpdateAvailable(false);
      // Re-arm the watcher so an even-newer build can trigger again.
      stoppedRef.current = false;
    },
  };
}
